import { useCanBuyGameKeyFn } from "@/hooks/useCanBuyGameKey";
import { useQuery } from "@tanstack/react-query";
import { produce } from "immer";
import ContentsService, { DeviceType } from "src/services/ContentsService";
import { useRouter } from "next/router";
import { create } from "zustand";
import { useMemo } from "react";
import { FilterType } from "@/v2-components/pages/games/_type";
import { GameQueryOptions } from "@/api/game-api";
import { GamesPlatformWebView } from "@/components/webview/games/constants/gameResellWebViewConstants";
import { GAME_SORT_BY_FALLBACK } from "@/constants/game-sort-options";
import { GAME_FILTER_GROUPS_FALLBACK } from "@/constants/game-filter-options";
import { WEBVIEW_ROUTES } from "@/components/webview/route";

type Store = {
  text: string;
  setText: (s: string) => void;
};

const useStore = create<Store>()((set) => ({
  text: "",
  setText: (text: string) => {
    set((state) => ({ text }));
  },
}));

// 웹뷰 기본 정렬(monthlyPlayCount)이 이 목록에 없으면 선택 라벨이 첫 옵션으로 잘못 표시된다
// (GamesSearchFiltersWebView.tsx 의 `?? sortOptions[0]`).
const defaultOptions = {
  gameKeysOnly: false,
  sortBy: GAME_SORT_BY_FALLBACK,
  // ⚠️ 손으로 나열하지 않는다 — 종전엔 여기와 `api/game-api.ts` · `api/game-api.server.ts`
  // 에 각각 적혀 **세 벌**이 갈라져 있었고, 셋 다 SDK 응답과 달랐다. 이 웹뷰 사본이 가장
  // 심해서 `sidequest`(SDK 에 없는 유령) · Support Type 2개(`official-mod`·`ai-powered`
  // 누락) · Device 그룹 통째 부재였다. 즉 옵션 호출이 실패하면 웹뷰에서 **필터 탭이 비고**
  // 「Official Mod」를 켜도 전체가 나온다.
  filters: GAME_FILTER_GROUPS_FALLBACK,
};

function useGetOptions() {
  return useQuery({
    queryKey: ["useGetOptions"],
    queryFn: async (): Promise<ContentsOptions | undefined> => {
      const res = await fetch(
        `/sdk-api/api/v2/bhaptics-contents/options/?platform=web`,
      );
      if (res.ok) {
        const unwrap = await res.json();
        if (unwrap.status) {
          return unwrap.message;
        }
        return defaultOptions;
      }
      return defaultOptions;
    },
  });
}

function useGetContents({
  filters,
  sortBy,
  onlySellable = false,
}: {
  filters: string[];
  sortBy: string;
  onlySellable?: boolean;
}) {
  return useQuery(
    GameQueryOptions.games({
      filters: filters.join(","),
      sortBy,
      onlySellable,
    }),
  );
}

export interface ContentsOptions {
  sortBy: {
    description: string;
    options: { value: string; description: string }[];
  };
  filters: {
    description: string;
    options: { value: string; description: string }[];
  }[];
}

export const useFilters = () => {
  const router = useRouter();
  const filter = router.query["filter"];
  const filters = filter?.toString().split(",") ?? ([] as string[]);

  const extra = router.pathname;
  if (extra.includes("meta-quest")) {
    filters.push("quest");
  } else if (extra.includes("steam-vr")) {
    filters.push("steam");
  } else if (extra.includes("ps-vr2")) {
    filters.push("psvr2");
  } else if (extra.includes("sim-racing")) {
    filters.push("simracing");
  } else if (extra.includes("pc")) {
    filters.push("pc");
  }

  return filters.filter((f) => !!f);
};

// 웹뷰(앱 내장) 기본 정렬은 의도적으로 monthlyPlayCount 유지 — 웹은 Recommended(popularity)로
// 바뀌었지만(#5064), 웹뷰 진입 링크들이 `&sort=monthlyPlayCount` 를 하드코딩하고 있어 훅 기본값만
// 바꾸면 진입 경로별로 정렬이 갈린다. 앱 릴리스/QA 사이클이 별개라 함께 바꿀 때 일괄 변경할 것.
export const useSortBy = (): string => {
  const router = useRouter();
  const sortBy = router.query["sort"] || "monthlyPlayCount";
  return sortBy.toString();
};
const useOnlyGameKey = () => {
  const router = useRouter();
  const gameKey = router.query["only-sellable"];
  const onlyGameKey = gameKey === "true" ? true : false;
  return onlyGameKey;
};

const DeviceTypePriority = [
  DeviceType.Vest,
  DeviceType.Arm,
  DeviceType.Head,
  DeviceType.Glove,
  DeviceType.Foot,
  DeviceType.Hand,
];

const DevicePriorityMap = new Map(
  DeviceTypePriority.map((type, index) => [type, index]),
);

function sortDevices(devices: DeviceType[]) {
  return devices.sort(
    (a, b) =>
      (DevicePriorityMap.get(a) ?? Infinity) -
      (DevicePriorityMap.get(b) ?? Infinity),
  );
}

// !!TODO api 변경되면 적용
export function useNewGames(platform?: GamesPlatformWebView) {
  const { data, isLoading, isFetching } = useGetContents({
    filters: platform ? [platform] : [],
    sortBy: "",
  });

  const isSimracing = platform === GamesPlatformWebView.Simracing;

  // mop 에서 Sim Racing은 모든 게임을 보여줍니다
  const newGames = isSimracing
    ? data
    : data?.filter((game) => game.badges.includes("New"));

  return { games: newGames ?? [], isLoading: isLoading || isFetching };
}

export function useSellableGames(platform?: string) {
  const { data, isLoading, isFetching } = useGetContents({
    filters: platform ? [platform] : [],
    sortBy: "",
    onlySellable: true,
  });
  // sdk `onlySellable` 은 게임 키 `enable` 만 본다 — 가격 없는 키(번들 전용 · #6053)도 들어온다. 「Game Key Sales」 목록에서
  // 살 수 없는 게임을 빼고, 카드와 같은 판정(`GameUtil.canBuyGameKey`)을 쓴다(#6054 R1).
  const canBuy = useCanBuyGameKeyFn();
  const games = useMemo(
    () => (data ?? []).filter((g) => canBuy(g.gameResell)),
    [data, canBuy],
  );
  return { games, isLoading: isLoading || isFetching };
}

// !!TODO 게임키워드 트렌드로 처리가 안될거 같아서 api 주간게임들 관련 api 를 기다리고 있습니다.
export function useWeeklyGames() {
  // const keywords = useSearchGamesTrendingKeywords()
  // console.log(keywords.data)
  const { data, isLoading, isFetching } = useGetContents({
    filters: [],
    sortBy: "",
  });
  const weeklyGames = data?.filter((_, i) => i < 9);
  return { games: weeklyGames ?? [], isLoading: isLoading || isFetching };
}

//TODO: 리팩토링 훅 쪼개기
export function useBhapticsContentsWebView() {
  const router = useRouter();

  const { text, setText } = useStore();
  const sortBy = useSortBy();
  const { data: options } = useGetOptions();
  const filters = useFilters();
  const onlySellable = useOnlyGameKey();
  const getContents = useGetContents({
    filters: filters,
    sortBy: sortBy,
    onlySellable,
  });

  // 「Game Key」 토글(`onlySellable`)이면 살 수 있는 게임 키만 — sdk 는 `enable` 만 본다(위 `useSellableGames` 와 같은 이유, #6054 R1)
  const canBuy = useCanBuyGameKeyFn();
  const filteredContents = useMemo(() => {
    return (
      getContents.data
        ?.filter((item) => !onlySellable || canBuy(item.gameResell))
        ?.filter((item) =>
          !text ? true : item.name.toLowerCase().includes(text.toLowerCase()),
        )
        ?.map((item) => {
          return produce(item, (draft) => {
            draft.compatibleDevices = sortDevices(draft.compatibleDevices);
            draft.supportedPlatforms.forEach((c, i) => {
              c.platform = ContentsService.friendlyPlatformName(c.platform);
            });
          });
        }) ?? []
    );
  }, [getContents.data, text, onlySellable, canBuy]);

  const _options = useMemo(() => {
    const friendly = ({
      value,
      description,
    }: {
      value: string;
      description: string;
    }) => {
      if (value === "Vest") {
        return { value, description: "TactSuit Pro & Air" };
      }
      if (value === "Arm") {
        return { value, description: "TactSleeve" };
      }
      if (value === "simracing") {
        return { value, description: "Sim Racing" };
      }
      return { value, description };
    };

    if (!!options) {
      const { sortBy, filters } = options;
      return {
        sortBy,
        filters: filters.map((filter) => ({
          ...filter,
          options: filter.options.map(friendly),
        })),
        gameKeysOnly: onlySellable,
      };
    }
    return defaultOptions;
  }, [options, onlySellable]);

  const reset = () => {
    router.push(
      {
        pathname: WEBVIEW_ROUTES.webviewSearch,
        query: { ...router.query, filter: "" },
      },
      undefined,
    );
  };

  const handleSetFilters = (values: string[], gameKey?: boolean) => {
    handleQueries(values, sortBy, gameKey);
  };

  const toggle = (value: string) => {
    let newFilter = filters.includes(value)
      ? filters.filter((f) => f !== value)
      : [...filters, value];
    handleSetFilters(newFilter, onlySellable);
  };

  const toggleGameKey = () => {
    const gameKey = router.query["only-sellable"];
    const value = gameKey === "true" ? false : true;
    handleSetFilters(filters, value);
  };

  const handleSortBy = (sort: string) => {
    handleQueries(filters, sort, onlySellable);
  };

  const handleQueries = (
    filters: string[],
    sortBy: string,
    gameKey = onlySellable,
  ) => {
    router.push(
      {
        pathname: WEBVIEW_ROUTES.webviewSearch,
        query: {
          ...router.query,
          filter: filters.join(","),
          sort: sortBy,
          "only-sellable": gameKey,
        },
      },
      undefined,
    );
  };

  const selectedFilters = useMemo(() => {
    const allOptions = _options.filters.flatMap((f) =>
      f.options.map((o) => ({
        value: o.value,
        label: o.description,
        type: f.description as FilterType,
      })),
    );
    return allOptions.filter((o) => filters.includes(o.value));
  }, [_options.filters, filters]);

  return {
    search: text,
    selectedFilters,
    onlySellable,
    isLoading: getContents.isLoading,
    resetFilters: () => reset(),
    setSearch: (s: string) => setText(s),
    toggleFilters: toggle,
    toggleGameKey,
    setFilters: handleSetFilters,
    setSortBy: handleSortBy,
    contents: filteredContents,
    rawContents: getContents.data,
    setQueries: handleQueries,
    sortBy,
    options: _options,
  };
}
